/**
 * Multi-tab synchronization service for token management
 * Handles token updates across multiple browser tabs using localStorage events
 */

export interface MultiTabSyncCallbacks {
  onTokenUpdate: (token: string, refreshToken: string) => void
  onLogout: () => void
  onUserUpdate: (user: any) => void
  onCSRFTokenUpdate?: (csrfToken: string) => void
}

export class MultiTabSyncService {
  private callbacks: MultiTabSyncCallbacks
  private isActive = false
  private storageKeys = {
    token: 'auth_token',
    refreshToken: 'refresh_token',
    user: 'user',
    tokenExpiresAt: 'token_expires_at',
    csrfToken: 'csrf_token',
    csrfExpiresAt: 'csrf_expires_at',
    syncEvent: 'auth_sync_event',
  }

  constructor(callbacks: MultiTabSyncCallbacks) {
    this.callbacks = callbacks
  }

  /**
   * Start listening for cross-tab synchronization events
   */
  start(): void {
    if (this.isActive) return

    this.isActive = true
    window.addEventListener('storage', this.handleStorageChange)
    window.addEventListener('beforeunload', this.handleBeforeUnload)

    // Send initial sync event to notify other tabs
    this.broadcastSyncEvent('tab_opened')
  }

  /**
   * Stop listening for cross-tab synchronization events
   */
  stop(): void {
    if (!this.isActive) return

    this.isActive = false
    window.removeEventListener('storage', this.handleStorageChange)
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  }

  /**
   * Handle localStorage changes from other tabs
   */
  private handleStorageChange = (event: StorageEvent) => {
    if (!this.isActive) return

    const { key, newValue, oldValue } = event

    // Handle token updates
    if (key === this.storageKeys.token && newValue !== oldValue) {
      if (newValue) {
        const refreshToken = localStorage.getItem(this.storageKeys.refreshToken)
        if (refreshToken) {
          this.callbacks.onTokenUpdate(newValue, refreshToken)
        }
      } else {
        // Token was removed, trigger logout
        this.callbacks.onLogout()
      }
    }

    // Handle refresh token updates
    if (key === this.storageKeys.refreshToken && newValue !== oldValue) {
      if (newValue) {
        const token = localStorage.getItem(this.storageKeys.token)
        if (token) {
          this.callbacks.onTokenUpdate(token, newValue)
        }
      } else {
        // Refresh token was removed, trigger logout
        this.callbacks.onLogout()
      }
    }

    // Handle user updates
    if (key === this.storageKeys.user && newValue !== oldValue) {
      if (newValue) {
        try {
          const user = JSON.parse(newValue)
          this.callbacks.onUserUpdate(user)
        } catch (error) {
          console.error('Failed to parse user data from storage:', error)
        }
      } else {
        // User was removed, trigger logout
        this.callbacks.onLogout()
      }
    }

    // Handle CSRF token updates
    if (key === this.storageKeys.csrfToken && newValue !== oldValue) {
      if (newValue && this.callbacks.onCSRFTokenUpdate) {
        this.callbacks.onCSRFTokenUpdate(newValue)
      }
    }

    // Handle sync events
    if (key === this.storageKeys.syncEvent && newValue) {
      try {
        const syncData = JSON.parse(newValue)
        this.handleSyncEvent(syncData)
      } catch (error) {
        console.error('Failed to parse sync event:', error)
      }
    }
  }

  /**
   * Handle sync events from other tabs
   */
  private handleSyncEvent(syncData: any): void {
    const { type, timestamp, tabId } = syncData

    // Ignore events from the same tab
    if (tabId === this.getTabId()) return

    switch (type) {
      case 'logout':
        this.callbacks.onLogout()
        break
      case 'token_refresh':
        // Another tab refreshed the token, update local state
        const token = localStorage.getItem(this.storageKeys.token)
        const refreshToken = localStorage.getItem(this.storageKeys.refreshToken)
        if (token && refreshToken) {
          this.callbacks.onTokenUpdate(token, refreshToken)
        }
        break
      case 'csrf_refresh':
        // Another tab refreshed the CSRF token, update local state
        const csrfToken = localStorage.getItem(this.storageKeys.csrfToken)
        if (csrfToken && this.callbacks.onCSRFTokenUpdate) {
          this.callbacks.onCSRFTokenUpdate(csrfToken)
        }
        break
      case 'tab_opened':
        // New tab opened, could be used for coordination
        break
    }
  }

  /**
   * Handle tab closing
   */
  private handleBeforeUnload = () => {
    this.broadcastSyncEvent('tab_closed')
  }

  /**
   * Broadcast a sync event to other tabs
   */
  broadcastSyncEvent(type: string, data?: any): void {
    if (!this.isActive) return

    const syncData = {
      type,
      timestamp: Date.now(),
      tabId: this.getTabId(),
      data,
    }

    try {
      localStorage.setItem(this.storageKeys.syncEvent, JSON.stringify(syncData))
      // Remove the event immediately to allow for repeated events
      setTimeout(() => {
        localStorage.removeItem(this.storageKeys.syncEvent)
      }, 100)
    } catch (error) {
      console.error('Failed to broadcast sync event:', error)
    }
  }

  /**
   * Broadcast token update to other tabs
   */
  broadcastTokenUpdate(token: string, refreshToken: string): void {
    this.broadcastSyncEvent('token_refresh', { token, refreshToken })
  }

  /**
   * Broadcast logout to other tabs
   */
  broadcastLogout(): void {
    this.broadcastSyncEvent('logout')
  }

  /**
   * Broadcast CSRF token update to other tabs
   */
  broadcastCSRFTokenUpdate(csrfToken: string): void {
    this.broadcastSyncEvent('csrf_refresh', { csrfToken })
  }

  /**
   * Get unique tab identifier
   */
  private getTabId(): string {
    if (!window.sessionStorage.getItem('tab_id')) {
      window.sessionStorage.setItem('tab_id', Math.random().toString(36).substr(2, 9))
    }
    return window.sessionStorage.getItem('tab_id') || 'unknown'
  }

  /**
   * Check if service is active
   */
  get isServiceActive(): boolean {
    return this.isActive
  }

  /**
   * Force sync with current localStorage state
   */
  forceSyncFromStorage(): void {
    const token = localStorage.getItem(this.storageKeys.token)
    const refreshToken = localStorage.getItem(this.storageKeys.refreshToken)
    const user = localStorage.getItem(this.storageKeys.user)
    const csrfToken = localStorage.getItem(this.storageKeys.csrfToken)

    if (token && refreshToken) {
      this.callbacks.onTokenUpdate(token, refreshToken)
    }

    if (user) {
      try {
        const userData = JSON.parse(user)
        this.callbacks.onUserUpdate(userData)
      } catch (error) {
        console.error('Failed to parse user data during force sync:', error)
      }
    }

    if (csrfToken && this.callbacks.onCSRFTokenUpdate) {
      this.callbacks.onCSRFTokenUpdate(csrfToken)
    }

    if (!token || !refreshToken || !user) {
      this.callbacks.onLogout()
    }
  }
}

/**
 * Create a multi-tab sync service instance
 */
export function createMultiTabSyncService(callbacks: MultiTabSyncCallbacks): MultiTabSyncService {
  return new MultiTabSyncService(callbacks)
}
